import { Percent } from "@mui/icons-material";
import type { FilterOption } from "components/shared/FiltersAutocomplete";
import FiltersAutocomplete from "components/shared/FiltersAutocomplete";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import StyledButton from "components/shared/StyledButton";
import { getCountries } from "countries";
import { replaceComma } from "helpers/utils";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { InvoicePercentage, PercentagesFilters } from "store/invoicePercentages";
import { createInvoicePercentage, updateInvoicePercentage } from "store/invoicePercentages";
import { useAppDispatch, useAppSelector } from "store/store";

import styles from "./style.module.scss";

interface InvoicePercentageForm {
    /**
     * If form is accesses on creation or update.
     */
    formMode: "new" | "edit" | undefined;
    /**
     * Data of the percentage for update.
     */
    invoicePercentage?: InvoicePercentage;
    /**
     * Closes modal once form success.
     */
    closeModal: () => Promise<void>;
}

interface FormData {
    name: string | undefined;
    percentage: string | undefined;
    filters?: PercentagesFilters;
}

// Within settings, form to create or update an invoicePercentage.
const InvoicePercentageForm = ({ formMode, invoicePercentage, closeModal }: InvoicePercentageForm) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const [chosenChannels, setChosenChannels] = useState([] as FilterOption[]);
    const [chosenCountries, setChosenCountries] = useState([] as FilterOption[]);

    const { channels } = useAppSelector((state) => state.channels);

    const channelsOptions = useMemo(() => {
        return channels.map((channel) => {
            return { id: channel.id, name: channel.name };
        });
    }, [channels]);

    const countriesOptions = getCountries().map((country) => ({ id: country.code, name: country.name }));

    const defaultValues = {
        name: formMode === "edit" ? invoicePercentage?.name : "",
        percentage: formMode === "edit" ? ((invoicePercentage?.percentage ?? 0) / 100).toString() : "0",
        filters: { channelIds: "all", countryCodes: "all" } as PercentagesFilters,
    };

    const {
        control,
        formState: { errors },
        handleSubmit,
    } = useForm({ defaultValues, mode: "all" });

    const onSubmit = async (data: FormData) => {
        const channelsId: number[] = chosenChannels.map((channel) => +channel.id);
        const countriesCode: string[] = chosenCountries.map((country) => country.id as string);

        const req = {
            name: data.name ?? "",
            percentage: Math.round(+replaceComma(data.percentage ?? "0")) * 100,
            filters: {
                channelIds: channelsId.length === 0 ? "all" : channelsId,
                countryCodes: countriesCode.length === 0 ? "all" : countriesCode,
            } as PercentagesFilters,
        };

        if (formMode === "new") {
            const response = await dispatch(createInvoicePercentage({ data: req }));
            if (response.meta.requestStatus === "fulfilled") {
                closeModal();
            }
        } else if (invoicePercentage) {
            const response = await dispatch(updateInvoicePercentage({ percentageId: invoicePercentage.id, data: req }));
            if (response.meta.requestStatus === "fulfilled") {
                closeModal();
            }
        }
    };

    useEffect(() => {
        if (formMode === "edit") {
            const filterChannels = invoicePercentage?.filters.channelIds;
            if (filterChannels !== "all") {
                const chosenChannels = (filterChannels as number[]).map((channel) => {
                    const channelData = channels.find((item) => item.id === channel);
                    return channelData ? { name: channelData.name, id: channel } : ({} as FilterOption);
                });
                chosenChannels && setChosenChannels(chosenChannels);
            }

            const filterCountries = invoicePercentage?.filters.countryCodes;
            if (filterCountries !== "all") {
                const chosenCountries = (filterCountries as string[]).map((country) => {
                    const countryData = getCountries().find((item) => item.code === country);
                    return countryData ? { name: countryData.name, id: country } : ({} as FilterOption);
                });

                chosenCountries && setChosenCountries(chosenCountries);
            }
        }
    }, [formMode, invoicePercentage]);

    return (
        <form className={styles["invoice-percentage-form"]} onSubmit={handleSubmit(onSubmit)}>
            <TextFormComponent
                control={control}
                controlName="name"
                label={t("InvoicePercentages.Form.CommissionName")}
                error={errors.name}
                required
            />
            <NumberFormComponent
                control={control}
                controlName="percentage"
                label={t("InvoicePercentages.Form.Percentage")}
                error={errors.percentage}
                float
                icon={<Percent />}
            />
            <FiltersAutocomplete
                label={t("InvoicePercentages.Form.Channels")}
                filtersOptions={channelsOptions}
                placeholder={t("InvoicePercentages.Form.ChooseChannels")}
                chosenOptions={chosenChannels}
                setChosenOptions={setChosenChannels}
                optionsLimit={-1}
            />
            <FiltersAutocomplete
                label={t("InvoicePercentages.Form.Countries")}
                filtersOptions={countriesOptions}
                placeholder={t("InvoicePercentages.Form.ChooseCountries")}
                chosenOptions={chosenCountries}
                setChosenOptions={setChosenCountries}
                optionsLimit={-1}
            />
            <StyledButton buttonType="primary" fullWidth type="submit" className="submit-btn">
                {formMode === "new" ? t("InvoicePercentages.Form.Confirm") : t("InvoicePercentages.Form.Update")}
            </StyledButton>
        </form>
    );
};

export default InvoicePercentageForm;
